/**
 * ResizeHandle — standalone extraction of the draggable divider used across the
 * QC/Sketch/Site-Analysis/Takeoff family of tools (QcMarkupModal.jsx's local `ResizeHandle`,
 * ~line 33 — dir-parameterized; SiteAnalysisModal.jsx keeps its own `PanelResizeHandle`,
 * dir hardcoded to -1). This is a faithful standalone copy of QcMarkupModal's version (the
 * more general one, supporting both drag directions via `dir`).
 *
 * Per docs/SUBMITTAL_REVIEW_PLAN.md Phase 3: added as a new shared copy for the Submittal
 * Review tool (Phase 4) to consume. QcMarkupModal and SiteAnalysisModal deliberately keep
 * their own local copies for now — converging the existing duplicates onto this file is
 * recorded backlog, not part of this change.
 *
 *  axis     — 'x' (default, a vertical bar dragged horizontally — the original/only mode)
 *             or 'y' (a horizontal bar dragged vertically, e.g. stacked panels). Added for
 *             AlderCAD's Properties/Chat dock split (ModelerPage.jsx) — SubmittalReviewModal,
 *             the other shared-copy consumer, is unaffected since it never passes this prop.
 *  dir      — +1 when dragging right/down widens the panel (a top/left-anchored drawer), -1
 *             when dragging left/up widens the panel (a bottom/right-anchored strip)
 *  getStart — returns the panel's current size (width or height) at drag start
 *  min/max  — clamp bounds; onChange(size) fires live during the drag; onCommit(size) fires
 *             once, on release (e.g. to persist the final size to localStorage)
 */
export default function ResizeHandle({ dir, getStart, min, max, onChange, onCommit, axis = 'x' }) {
  const isY = axis === 'y'
  function down(e) {
    e.preventDefault()
    const startPos = isY ? e.clientY : e.clientX
    const startSize = getStart()
    let last = startSize
    function move(ev) {
      const pos = isY ? ev.clientY : ev.clientX
      let s = startSize + dir * (pos - startPos)
      s = Math.max(min, Math.min(max, s))
      last = s
      onChange(s)
    }
    function up() {
      document.removeEventListener('pointermove', move)
      document.removeEventListener('pointerup', up)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      onCommit && onCommit(last)
    }
    document.addEventListener('pointermove', move)
    document.addEventListener('pointerup', up)
    document.body.style.cursor = isY ? 'row-resize' : 'col-resize'
    document.body.style.userSelect = 'none'
  }
  return (
    <div
      onPointerDown={down}
      title="Drag to resize"
      style={isY
        ? { height: 6, flexShrink: 0, cursor: 'row-resize', background: 'var(--aj-bg-muted)', borderTop: '0.5px solid var(--aj-border)', borderBottom: '0.5px solid var(--aj-border)' }
        : { width: 6, flexShrink: 0, cursor: 'col-resize', background: 'var(--aj-bg-muted)', borderLeft: '0.5px solid var(--aj-border)', borderRight: '0.5px solid var(--aj-border)' }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--aj-accent)' }}
      onMouseLeave={e => { e.currentTarget.style.background = 'var(--aj-bg-muted)' }}
    />
  )
}
